import { Button } from '@repo/ui/components/button';
import { Calendar } from '@repo/ui/components/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@repo/ui/components/popover';
import { IconCalendar, IconX } from '@tabler/icons-react';
import { useState } from 'react';

/**
 * Choosing a trading day.
 *
 * The registry publishes `calendar` and `popover` but **not** a date picker —
 * like the data table, that one is a composition guide rather than an
 * installable component, so this is the composition rather than a component
 * hand-written past something the registry already gives. `calendar` is
 * `react-day-picker` underneath and is not touched here.
 *
 * ## Why not the native `<input type="date">` it replaces
 *
 * The native control was the right first answer and its reasoning still holds:
 * its value is already `YYYY-MM-DD` and there is no time in it to send by
 * accident. What it is not is *this* app — it renders as the operating system's
 * own widget, in the operating system's type, and on a screen built out of
 * enamel plates and gauge hairlines it reads as a hole in the page. Dee asked
 * for the registry's picker after meeting it.
 *
 * The safety the native input gave for free is kept in `business-date.ts`
 * instead: `toCalendarDate` and `fromCalendarDate` are the only place a trading
 * day is allowed to become a `Date`, and the file says at length why they are
 * local-midnight rather than UTC. Nothing here does date arithmetic.
 *
 * ## Clearing
 *
 * "Every day" is a real answer and the default one, so clearing is a control of
 * its own rather than clicking the selected square again — a toggle nobody can
 * see is not a control. It appears only when there is something to clear.
 */
export function DayPicker({
  value,
  onValueChange,
  id,
  emptyLabel = 'Every day',
  format,
  toDate,
  fromDate,
  maxDay,
}: {
  /** `YYYY-MM-DD`, or `''` for no day chosen. */
  readonly value: string;

  onValueChange: (value: string) => void;

  readonly id?: string;

  /** What the trigger reads when nothing is chosen. */
  readonly emptyLabel?: string;

  /** How a chosen day is written out — `formatBusinessDate` at the call site. */
  format: (value: string) => string;

  /** `YYYY-MM-DD` → `Date | undefined`, from the module that owns the format. */
  fromDate: (value: string) => Date | undefined;

  /** `Date` → `YYYY-MM-DD`, its exact inverse. */
  toDate: (at: Date) => string;

  /**
   * The last day that may be chosen, `YYYY-MM-DD`. Days after it are offered
   * but refused, the way `react-day-picker` renders a disabled day.
   *
   * Optional and absent by default, because most pickers in this app bound
   * nothing: the tickets and sales filters are asking about the past *and* the
   * present, and a picker that greyed out tomorrow would be stating a rule
   * neither route has. The Reporting screens do have one — the period "may not
   * end after the current business day", refused `REPORTING_PERIOD_IN_FUTURE` —
   * and a bound here turns that from a round trip into a day nobody can press.
   * The refusal notice under the pickers stays: this is the first line of
   * defence and not the only one, since the park's day rolls at midnight in
   * Kolkata and a screen left open overnight would otherwise bound itself at
   * yesterday.
   *
   * Passed as a calendar date rather than a `Date` so this file still does no
   * date arithmetic and no parsing of its own — `fromDate`, which the caller
   * already supplies, is the one place that conversion happens.
   */
  maxDay?: string;
}) {
  const [open, setOpen] = useState(false);
  const selected = fromDate(value);
  const latest = maxDay === undefined ? undefined : fromDate(maxDay);

  return (
    <div className="flex items-center gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          render={
            <Button
              id={id}
              variant="outline"
              // Left, not centred: it sits in a row of filters whose labels
              // and values all start at the same edge, and a date that moves as
              // its length changes is the one thing in that row that jitters.
              //
              // `flex-1 min-w-0` and **not** `w-full`, which is what this was
              // until FE066 and which only worked where something else had
              // already decided how wide the picker is. `w-full` on a flex item
              // measures 100% of a width that is still being solved for, so the
              // row's own content width came out as the trigger alone and the
              // clear button beside it hung ~25px outside the box — invisible in
              // the `16rem` rail, where the rail stated the width, and a button
              // sitting on top of the fact next to it once the rail became a
              // heading band. Flexing from a zero basis states the same
              // intention and measures correctly at either.
              //
              // `bg-background` and not `bg-card`, which is the inversion the
              // sheet asks for and only there. `docs/design-language.md` says a
              // field is enamel on mist, and this picker lives in the toolbar of
              // an enamel sheet — where enamel on enamel is not a field at all,
              // just a button-shaped outline. The signed-out page keeps the mist
              // ground and its enamel fields; nothing there changes.
              className="h-10 min-w-0 flex-1 justify-start bg-background font-normal"
            >
              <IconCalendar data-icon="inline-start" className="text-muted-foreground" />
              {value === '' ? (
                <span className="text-muted-foreground">{emptyLabel}</span>
              ) : (
                format(value)
              )}
            </Button>
          }
        />

        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selected}
            // The month a picker opens on is the chosen day, or whatever
            // `react-day-picker` decides — which is the device's idea of today.
            // That is a *view* and not a value: nothing it highlights reaches the
            // API, and the day only changes when somebody picks one.
            defaultMonth={selected}
            disabled={latest === undefined ? undefined : { after: latest }}
            onSelect={(at) => {
              // `undefined` arrives when the selected day is clicked again.
              // Treated as no change rather than as a clear, because the visible
              // control below is what clears and two ways to do it is one too
              // many.
              if (at === undefined) return;

              onValueChange(toDate(at));
              setOpen(false);
            }}
            autoFocus
          />
        </PopoverContent>
      </Popover>

      {value !== '' && (
        <Button
          variant="ghost"
          size="icon"
          className="shrink-0"
          aria-label={`Clear the day, showing ${emptyLabel.toLowerCase()}`}
          onClick={() => {
            onValueChange('');
          }}
        >
          <IconX />
        </Button>
      )}
    </div>
  );
}
